import React, { useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import ImportFileUploadStep from "@/components/imports/ImportFileUploadStep";
import ImportMappingStep from "@/components/imports/ImportMappingStep";
import ImportPreviewStep from "@/components/imports/ImportPreviewStep";
import ImportProgressStep from "@/components/imports/ImportProgressStep";
import ImportValuesStep from "@/components/imports/ImportValuesStep";
import type {
  ImportEntityType,
  ImportMappingSpec,
  ImportPreviewRow,
  ImportSetup,
  ImportValueField,
} from "@/model/interface/DataImport";
import {
  useCreateImportMutation,
  useGetImportValuesMutation,
  usePreviewImportMutation,
  useRunImportMutation,
  useSelectImportSheetMutation,
  useUploadFilesMutation,
} from "@/store/rest_api";
import { apiErrorMessage } from "@/util/apiError";
import { cn } from "@/lib/utils";

type ImportStep = "upload" | "map" | "values" | "preview" | "progress";

interface ImportWizardProps {
  open: boolean;
  onClose: () => void;
  entityType: ImportEntityType;
  contactType?: "LEAD" | "CONTACT";
}

const EMPTY_MAPPING: ImportMappingSpec = { columns: {}, values: {}, listIds: [], tagNames: [], dealStageId: null };

const STEPS: { step: ImportStep; label: string }[] = [
  { step: "upload", label: "Choose a file" },
  { step: "map", label: "Match columns" },
  { step: "values", label: "Match values" },
  { step: "preview", label: "Review" },
  { step: "progress", label: "Import" },
];

function wizardTitle(entityType: ImportEntityType, contactType?: string): string {
  switch (entityType) {
    case "CONTACT":
      return contactType === "LEAD" ? "Import leads" : "Import contacts";
    case "PROPERTY":
      return "Import properties";
    case "DNC":
      return "Import Do Not Contact numbers";
    case "TASK":
      return "Import tasks";
    case "NOTE":
      return "Import notes and history";
  }
}

function wizardDescription(entityType: ImportEntityType, contactType?: string): string {
  switch (entityType) {
    case "CONTACT":
      return contactType === "LEAD"
        ? "Bring leads over from a spreadsheet or another CRM. A row that matches an existing lead by email, or by phone and name, updates it instead of creating a duplicate."
        : "Bring contacts over from a spreadsheet or another CRM. A row that matches an existing contact by email, or by phone and name, updates it instead of creating a duplicate.";
    case "PROPERTY":
      return "One row per property, with up to two owners. Owners become leads linked to the property, and an owner on several rows becomes one lead.";
    case "DNC":
      return "One phone number per row. Numbers already on your Do Not Contact list are skipped.";
    case "TASK":
      return "Each task links to its lead or contact by Retreevo ID, email, or phone and name. Rows without a match are skipped.";
    case "NOTE":
      return "Notes, call logs and text logs become dated notes on the matching leads. Nothing is sent or dialed.";
  }
}

export default function ImportWizard({ open, onClose, entityType, contactType }: ImportWizardProps) {
  const isPeopleImport = entityType === "CONTACT" || entityType === "PROPERTY";
  const recordNoun = entityType === "CONTACT" && contactType !== "LEAD" ? "contacts" : "leads";
  const [step, setStep] = useState<ImportStep>("upload");
  const [file, setFile] = useState<File | null>(null);
  const [setup, setSetup] = useState<ImportSetup | null>(null);
  const [mapping, setMapping] = useState<ImportMappingSpec>(EMPTY_MAPPING);
  const [valueFields, setValueFields] = useState<ImportValueField[]>([]);
  const [previewRows, setPreviewRows] = useState<ImportPreviewRow[]>([]);
  const [scrubFederalDnc, setScrubFederalDnc] = useState(true);
  const [runWorkflows, setRunWorkflows] = useState(false);
  const [consentConfirmed, setConsentConfirmed] = useState(false);
  const [saveTemplate, setSaveTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [runId, setRunId] = useState<string | null>(null);

  const [uploadFiles, { isLoading: isUploading }] = useUploadFilesMutation();
  const [createImport, { isLoading: isCreating }] = useCreateImportMutation();
  const [selectImportSheet, { isLoading: isSelectingSheet }] = useSelectImportSheetMutation();
  const [getImportValues, { isLoading: isLoadingValues }] = useGetImportValuesMutation();
  const [previewImport, { isLoading: isPreviewing }] = usePreviewImportMutation();
  const [runImport, { isLoading: isStarting }] = useRunImportMutation();

  const busy = isUploading || isCreating || isSelectingSheet || isLoadingValues || isPreviewing || isStarting;
  const stepIndex = STEPS.findIndex((entry) => entry.step === step);

  function resetWizard() {
    setStep("upload");
    setFile(null);
    setSetup(null);
    setMapping(EMPTY_MAPPING);
    setValueFields([]);
    setPreviewRows([]);
    setScrubFederalDnc(true);
    setRunWorkflows(false);
    setConsentConfirmed(false);
    setSaveTemplate(false);
    setTemplateName("");
    setRunId(null);
  }

  function closeWizard() {
    onClose();
    resetWizard();
  }

  function applySetup(nextSetup: ImportSetup) {
    setSetup(nextSetup);
    setMapping(nextSetup.mapping);
    setValueFields([]);
  }

  async function handleUpload() {
    if (!file) {
      return;
    }
    try {
      const uploadedFiles = await uploadFiles({ files: [file], type: "data_import" }).unwrap();
      const fileId = uploadedFiles[0]?.id;
      if (!fileId) {
        toast.error("The file could not be uploaded");
        return;
      }
      const createdSetup = await createImport({
        fileId,
        entityType,
        contactType: entityType === "CONTACT" ? contactType : null,
      }).unwrap();
      applySetup(createdSetup);
      setStep("map");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The file could not be uploaded"));
    }
  }

  async function handleSheetChange(sheetName: string) {
    if (!setup) {
      return;
    }
    try {
      applySetup(await selectImportSheet({ importId: setup.importId, sheetName }).unwrap());
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The sheet could not be read"));
    }
  }

  async function loadPreview(importId: string) {
    const rows = await previewImport({ importId, mapping }).unwrap();
    setPreviewRows(rows);
    setStep("preview");
  }

  async function handleMappingContinue() {
    if (!setup) {
      return;
    }
    try {
      const fields = await getImportValues({ importId: setup.importId, mapping }).unwrap();
      setValueFields(fields);
      if (fields.length > 0) {
        setStep("values");
        return;
      }
      await loadPreview(setup.importId);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The file could not be read"));
    }
  }

  async function handleValuesContinue() {
    if (!setup) {
      return;
    }
    try {
      await loadPreview(setup.importId);
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The preview could not be built"));
    }
  }

  async function handleStart() {
    if (!setup) {
      return;
    }
    const chosenTemplateName = templateName.trim() || setup.fileName?.replace(/\.[^.]+$/, "") || "Saved mapping";
    try {
      const run = await runImport({
        importId: setup.importId,
        mapping,
        options: {
          contactType: setup.contactType ?? null,
          scrubFederalDnc: isPeopleImport && scrubFederalDnc,
          runWorkflows: isPeopleImport && runWorkflows,
          consentConfirmed: isPeopleImport && consentConfirmed,
          templateName: saveTemplate ? chosenTemplateName : null,
        },
      }).unwrap();
      setRunId(run.id);
      setStep("progress");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "The import could not start"));
    }
  }

  function handleBack() {
    if (step === "map") {
      setStep("upload");
    }
    if (step === "values") {
      setStep("map");
    }
    if (step === "preview") {
      setStep(valueFields.length > 0 ? "values" : "map");
    }
  }

  function handleContinue() {
    if (step === "upload") {
      handleUpload();
    }
    if (step === "map") {
      handleMappingContinue();
    }
    if (step === "values") {
      handleValuesContinue();
    }
    if (step === "preview") {
      handleStart();
    }
  }

  const canContinue =
    !busy && (step !== "upload" || (file !== null && (!isPeopleImport || consentConfirmed)));

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => (!nextOpen ? closeWizard() : null)}>
      <DialogContent className="max-w-4xl text-foreground" onInteractOutside={(event) => event.preventDefault()}>
        <DialogHeader>
          <DialogTitle>{wizardTitle(entityType, contactType)}</DialogTitle>
          <DialogDescription asChild>
            <ol className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
              {STEPS.map((entry, index) => (
                <li
                  key={entry.step}
                  className={cn(
                    index === stepIndex ? "font-semibold text-foreground" : "text-muted-foreground",
                    index > stepIndex && "opacity-60",
                  )}
                >
                  {index + 1}. {entry.label}
                </li>
              ))}
            </ol>
          </DialogDescription>
        </DialogHeader>

        {step === "upload" && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">{wizardDescription(entityType, contactType)}</p>
            <ImportFileUploadStep file={file} busy={busy} onFileSelect={setFile} />
            {isPeopleImport && (
              <div className="space-y-3">
                <div className="flex items-start gap-3 rounded-[var(--radius)] border border-border p-4">
                  <Switch
                    id="import-scrub-federal-dnc"
                    checked={scrubFederalDnc}
                    onCheckedChange={setScrubFederalDnc}
                    disabled={busy}
                  />
                  <div className="space-y-0.5">
                    <label htmlFor="import-scrub-federal-dnc" className="text-sm font-medium">
                      Scrub against Federal DNC
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Check every imported number against the National Do Not Call registry. Billed per number,
                      including reused results.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-[var(--radius)] border border-border p-4">
                  <Switch
                    id="import-run-workflows"
                    checked={runWorkflows}
                    onCheckedChange={setRunWorkflows}
                    disabled={busy}
                  />
                  <div className="space-y-0.5">
                    <label htmlFor="import-run-workflows" className="text-sm font-medium">
                      Run workflows for imported {recordNoun}
                    </label>
                    <p className="text-xs text-muted-foreground">
                      Each imported record fires your active workflows, which can start sequences, send messages, or
                      queue dials. Leave off when migrating an old list.
                    </p>
                  </div>
                </div>
                <label className="flex items-start gap-2 text-sm">
                  <Checkbox
                    checked={consentConfirmed}
                    onCheckedChange={(checked) => setConsentConfirmed(checked === true)}
                    disabled={busy}
                    className="mt-0.5"
                  />
                  <span>
                    I confirm I have the right to contact the people in this file, and I will honor Do Not Call requests
                    and opt-outs.
                  </span>
                </label>
              </div>
            )}
          </div>
        )}

        {step === "map" && setup && (
          <ImportMappingStep
            setup={setup}
            mapping={mapping}
            busy={busy}
            saveTemplate={saveTemplate}
            templateName={templateName}
            onMappingChange={setMapping}
            onSheetChange={handleSheetChange}
            onSaveTemplateChange={setSaveTemplate}
            onTemplateNameChange={setTemplateName}
          />
        )}

        {step === "values" && (
          <ImportValuesStep valueFields={valueFields} mapping={mapping} onMappingChange={setMapping} />
        )}

        {step === "preview" && setup && <ImportPreviewStep rowCount={setup.rowCount} previewRows={previewRows} />}

        {step === "progress" && runId && <ImportProgressStep runId={runId} onNavigate={closeWizard} />}

        <DialogFooter className="gap-2 sm:justify-between">
          <div>
            {(step === "map" || step === "values" || step === "preview") && (
              <Button variant="ghost" onClick={handleBack} disabled={busy}>
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          {step === "progress" ? (
            <Button onClick={closeWizard}>Done</Button>
          ) : (
            <Button onClick={handleContinue} disabled={!canContinue}>
              {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {step === "preview" ? "Start import" : "Continue"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
